/**
 * FaithCheckin.js
 *
 * Exports:
 *   <FaithCheckinProvider>  — wrap around app; manages popup timing
 *   useFaithCheckin()       — { openCheckin } for manual trigger
 *   <FaithCheckinChart>     — 90-day trajectory chart for profile
 */

import React, { useState, useEffect, useContext, createContext, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from '../i18n';
import './FaithCheckin.css';
import { api } from '../api/client';

// ── Constants ────────────────────────────────────────────────────────────────

// 7-point scale: 1=Crisis → 7=Close
const LABELS = ['Crisis', 'Distant', 'Going through\nthe motions', 'Trying', 'Present', 'Connected', 'Close'];
const LABELS_SHORT = ['Crisis', 'Distant', 'Motions', 'Trying', 'Present', 'Connected', 'Close'];
const EMOJIS = ['😞', '😶', '😑', '🙏', '😌', '🙂', '🥰'];
const COLORS = ['#EF4444', '#F97316', '#FBBF24', '#A3E635', '#34D399', '#22C55E', '#10B981'];
const ACCENT = ['#FEE2E2', '#FFEDD5', '#FEF9C3', '#ECFCCB', '#D1FAE5', '#BBF7D0', '#6EE7B7'];

// ── Context ──────────────────────────────────────────────────────────────────

const FaithCheckinContext = createContext(null);

export function useFaithCheckin() {
  return useContext(FaithCheckinContext) || {};
}

// ── Provider (manages daily popup timing) ────────────────────────────────────

export function FaithCheckinProvider({ children }) {
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    // Auto-open if push notification deep-linked here
    const params = new URLSearchParams(window.location.search);
    if (params.get('checkin') === '1') {
      setShowModal(true);
      // Clean the param without reloading
      const clean = window.location.pathname + (window.location.search.replace(/[?&]checkin=1/,'') || '');
      window.history.replaceState({}, '', clean);
      return;
    }

    // Daily popup: skip if already dismissed today
    const key = 'fc_dismissed_' + new Date().toDateString();
    if (localStorage.getItem(key)) return;

    const t = setTimeout(async () => {
      try {
        const d = await api.get('/api/faith-checkins?days=1');
        if (!d.todayCheckin) setShowModal(true);
      } catch {}
    }, 2500);
    return () => clearTimeout(t);
  }, []);

  function handleDismiss() {
    const key = 'fc_dismissed_' + new Date().toDateString();
    localStorage.setItem(key, '1');
    setShowModal(false);
  }

  return (
    <FaithCheckinContext.Provider value={{ openCheckin: () => setShowModal(true) }}>
      {children}
      {showModal && (
        <FaithCheckinModal
          onClose={handleDismiss}
          onSaved={handleDismiss}
        />
      )}
    </FaithCheckinContext.Provider>
  );
}

// ── Modal ─────────────────────────────────────────────────────────────────────

function FaithCheckinModal({ onClose, onSaved }) {
  const { t } = useTranslation('faithCheckin');
  const [score,   setScore]   = useState(4); // 1-7, default middle
  const [note,    setNote]    = useState('');
  const [saving,  setSaving]  = useState(false);
  const [done,    setDone]    = useState(false);

  const labels      = t('labels');
  const labelsShort = t('labelsShort');

  async function handleSave() {
    if (!score) return;
    setSaving(true);
    try {
      await api.post('/api/faith-checkins', { score, note: note.trim() || undefined });
      setDone(true);
      setTimeout(onSaved, 1400);
    } catch {
      setSaving(false);
    }
  }

  // Glow color from red (score 1) → green (score 7)
  const glowColor = COLORS[score - 1];

  // Done confirmation screen
  if (done) {
    return (
      <div className="fc-overlay">
        <div className="fc-modal fc-modal-done">
          <div className="fc-done-icon">{EMOJIS[(score||4)-1]}</div>
          <p className="fc-done-text">{t('done')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fc-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="fc-modal">
        {/* Header */}
        <div className="fc-modal-header">
          <div>
            <h2 className="fc-modal-title">{t('modalTitle')}</h2>
            <p className="fc-modal-sub">{t('modalSub')}</p>
          </div>
          <button className="fc-close" onClick={onClose}>✕</button>
        </div>

        {/* Emoji display — natural color with glow */}
        <div className="fc-emoji-display">
          <span
            className="fc-big-emoji"
            style={{ filter: `drop-shadow(0 0 10px ${glowColor}99) drop-shadow(0 0 22px ${glowColor}55)` }}
          >
            {EMOJIS[score-1]}
          </span>
          <span className="fc-emoji-label" style={{ color: glowColor }}>
            {Array.isArray(labelsShort) ? labelsShort[score-1] : LABELS_SHORT[score-1]}
          </span>
        </div>

        {/* Slider */}
        <div className="fc-slider-wrap">
          <span className="fc-slider-end-label">{t('endLow')}</span>
          <div className="fc-slider-track-wrap">
            <div className="fc-slider-gradient"/>
            <input
              type="range"
              className="fc-slider"
              min={1} max={7} step={1}
              value={score}
              onChange={e => setScore(Number(e.target.value))}
              style={{ '--thumb-color': glowColor }}
            />
          </div>
          <span className="fc-slider-end-label">{t('endHigh')}</span>
        </div>

        {/* Tick marks */}
        <div className="fc-ticks">
          {[1,2,3,4,5,6,7].map(v => (
            <button key={v} className={`fc-tick ${score === v ? 'active' : ''}`}
              style={score === v ? { background: COLORS[v-1] } : {}}
              onClick={() => setScore(v)}>{v}</button>
          ))}
        </div>

        {/* Optional note */}
        <textarea
          className="fc-note"
          placeholder={t('notePlaceholder')}
          value={note}
          onChange={e => setNote(e.target.value)}
          rows={2}
          maxLength={200}
        />

        {/* Actions */}
        <div className="fc-actions">
          <button className="fc-skip" onClick={async () => {
            try { await api.post('/api/faith-checkins', { skipped: true }); } catch {}
            onClose();
          }}>{t('skip')}</button>
          <button
            className="fc-save btn btn-primary"
            disabled={saving}
            onClick={handleSave}
          >
            {saving ? <span className="spinner" style={{width:14,height:14,borderColor:'white',borderTopColor:'transparent'}}/> : t('save')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Profile Chart ─────────────────────────────────────────────────────────────

export function FaithCheckinChart({ onClose }) {
  const { t } = useTranslation('faithCheckin');
  const [checkins, setCheckins] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [showModal, setShowModal] = useState(false);
  const canvasRef = useRef(null);

  useEffect(() => {
    api.get('/api/faith-checkins?days=90')
      .then(d => setCheckins(d.checkins || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Draw chart whenever checkins change
  useEffect(() => {
    if (!canvasRef.current || checkins.length === 0) return;
    drawChart(canvasRef.current, checkins);
  }, [checkins]);

  function drawChart(canvas, data) {
    const dpr = window.devicePixelRatio || 1;
    const W = canvas.offsetWidth;
    const H = canvas.offsetHeight;
    canvas.width  = W * dpr;
    canvas.height = H * dpr;
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);

    const PAD = { top: 16, right: 16, bottom: 28, left: 28 };
    const cW = W - PAD.left - PAD.right;
    const cH = H - PAD.top  - PAD.bottom;

    // Y grid lines (scores 1–5)
    ctx.strokeStyle = getComputedStyle(document.body).getPropertyValue('--gray-1') || '#F1F5F9';
    ctx.lineWidth = 1;
    for (let s = 1; s <= 7; s++) {
      const y = PAD.top + cH - ((s - 1) / 6) * cH;
      ctx.beginPath(); ctx.moveTo(PAD.left, y); ctx.lineTo(PAD.left + cW, y); ctx.stroke();
      // Label
      ctx.fillStyle = '#94A3B8';
      ctx.font = `${10 * dpr / dpr}px system-ui, sans-serif`;
      ctx.textAlign = 'right';
      ctx.fillText((LABELS_SHORT[s-1]||'').slice(0,4), PAD.left - 4, y + 4);
    }

    if (data.length < 2) {
      // Single point — just draw a dot
      const d = data[0];
      const x = PAD.left + cW / 2;
      const y = PAD.top + cH - ((d.score - 1) / 6) * cH;
      ctx.beginPath();
      ctx.arc(x, y, 5, 0, Math.PI * 2);
      ctx.fillStyle = COLORS[d.score - 1];
      ctx.fill();
      return;
    }

    // Points
    const points = data.map((d, i) => ({
      x: PAD.left + (i / (data.length - 1)) * cW,
      y: PAD.top + cH - ((d.score - 1) / 6) * cH,
      score: d.score,
      date: new Date(d.checkedAt),
    }));

    // Filled gradient area under the line
    const grad = ctx.createLinearGradient(0, PAD.top, 0, PAD.top + cH);
    grad.addColorStop(0, 'rgba(16,185,129,0.18)');
    grad.addColorStop(1, 'rgba(16,185,129,0.00)');
    ctx.beginPath();
    ctx.moveTo(points[0].x, PAD.top + cH);
    points.forEach(p => ctx.lineTo(p.x, p.y));
    ctx.lineTo(points[points.length - 1].x, PAD.top + cH);
    ctx.closePath();
    ctx.fillStyle = grad;
    ctx.fill();

    // Smooth line
    ctx.beginPath();
    ctx.moveTo(points[0].x, points[0].y);
    for (let i = 1; i < points.length; i++) {
      const cpx = (points[i-1].x + points[i].x) / 2;
      ctx.bezierCurveTo(cpx, points[i-1].y, cpx, points[i].y, points[i].x, points[i].y);
    }
    ctx.strokeStyle = '#10B981';
    ctx.lineWidth = 2.5;
    ctx.lineJoin = 'round';
    ctx.stroke();

    // Dots
    points.forEach(p => {
      ctx.beginPath();
      ctx.arc(p.x, p.y, 4, 0, Math.PI * 2);
      ctx.fillStyle = COLORS[p.score - 1];
      ctx.strokeStyle = 'white';
      ctx.lineWidth = 2;
      ctx.fill();
      ctx.stroke();
    });

    // X-axis date labels — show first, last, and every ~30 days
    ctx.fillStyle = '#94A3B8';
    ctx.font = `10px system-ui, sans-serif`;
    ctx.textAlign = 'center';
    points.forEach((p, i) => {
      if (i === 0 || i === points.length - 1 || i % Math.floor(data.length / 3) === 0) {
        ctx.fillText(
          p.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          p.x, PAD.top + cH + 18
        );
      }
    });
  }

  const latest   = checkins[checkins.length - 1];
  const avg      = checkins.length ? (checkins.reduce((s, c) => s + c.score, 0) / checkins.length).toFixed(1) : null;
  const trend    = checkins.length >= 7
    ? checkins.slice(-7).reduce((s,c) => s + c.score, 0) / 7
    : null;
  const trendDir = trend && avg ? (trend > avg ? '↑' : trend < avg ? '↓' : '→') : null;

  return (
    <div className="page">
      <div className="fc-chart-toolbar">
        {onClose && (
          <button className="gp-back-btn" onClick={onClose}>
            {t('back')}
          </button>
        )}
        <button className="btn btn-primary" style={{gap:6,marginLeft:'auto'}} onClick={() => setShowModal(true)}>
          {t('checkInBtn')}
        </button>
      </div>

      <div className="fc-chart-header">
        <h1 className="fc-chart-title">{t('chartTitle')}</h1>
        <p className="fc-chart-sub">{t('chartSub')}</p>
      </div>

      {loading ? (
        <div style={{display:'flex',justifyContent:'center',padding:48}}>
          <span className="spinner" style={{width:28,height:28}}/>
        </div>
      ) : checkins.length === 0 ? (
        <div className="fc-chart-empty">
          <p className="fc-chart-empty-emoji">🌱</p>
          <p>{t('emptyHint')}</p>
          <button className="btn btn-primary" style={{marginTop:16}} onClick={() => setShowModal(true)}>
            {t('checkInNow')}
          </button>
        </div>
      ) : (
        <>
          {/* Summary pills */}
          <div className="fc-summary-row">
            {latest && (
              <div className="fc-summary-pill" style={{ borderColor: COLORS[latest.score-1] }}>
                <span className="fc-sum-emoji">{EMOJIS[latest.score-1]}</span>
                <div>
                  <div className="fc-sum-label">{t('lastCheckin')}</div>
                  <div className="fc-sum-val" style={{color: COLORS[latest.score-1]}}>{LABELS[latest.score-1]}</div>
                </div>
              </div>
            )}
            {avg && (
              <div className="fc-summary-pill">
                <span className="fc-sum-emoji">📊</span>
                <div>
                  <div className="fc-sum-label">{t('avg90')}</div>
                  <div className="fc-sum-val">{avg} / 5</div>
                </div>
              </div>
            )}
            {trendDir && (
              <div className="fc-summary-pill">
                <span className="fc-sum-emoji">{trendDir === '↑' ? '📈' : trendDir === '↓' ? '📉' : '➡️'}</span>
                <div>
                  <div className="fc-sum-label">{t('trend7')}</div>
                  <div className="fc-sum-val" style={{color: trendDir === '↑' ? '#10B981' : trendDir === '↓' ? '#EF4444' : '#94A3B8'}}>
                    {trendDir} {trendDir === '↑' ? t('closer') : trendDir === '↓' ? t('movingAway') : t('steady')}
                  </div>
                </div>
              </div>
            )}
            <div className="fc-summary-pill">
              <span className="fc-sum-emoji">📅</span>
              <div>
                <div className="fc-sum-label">{t('checkins')}</div>
                <div className="fc-sum-val">{checkins.length} days</div>
              </div>
            </div>
          </div>

          {/* Chart */}
          <div className="fc-chart-wrap card">
            <canvas ref={canvasRef} className="fc-canvas"/>
          </div>

          {/* Recent entries */}
          <h3 className="fc-entries-title">{t('recentEntries')}</h3>
          <div className="fc-entries">
            {[...checkins].reverse().slice(0, 14).map(c => (
              <div key={c.id} className="fc-entry">
                <div className="fc-entry-dot" style={{background: COLORS[c.score-1]}}>
                  {EMOJIS[c.score-1]}
                </div>
                <div className="fc-entry-body">
                  <span className="fc-entry-label" style={{color: COLORS[c.score-1]}}>{Array.isArray(t('labels')) ? t('labels')[c.score-1] : LABELS[c.score-1]}</span>
                  {c.note && <span className="fc-entry-note">{c.note}</span>}
                </div>
                <span className="fc-entry-date">
                  {new Date(c.checkedAt).toLocaleDateString('en-US', { month:'short', day:'numeric' })}
                </span>
              </div>
            ))}
          </div>
        </>
      )}

      {showModal && (
        <FaithCheckinModal
          onClose={() => setShowModal(false)}
          onSaved={() => {
            setShowModal(false);
            // Reload
            api.get('/api/faith-checkins?days=90')
              .then(d => setCheckins(d.checkins || []))
              .catch(() => {});
          }}
        />
      )}
    </div>
  );
}

// ── Profile block (teaser card on profile) ────────────────────────────────────

export function FaithCheckinProfileBlock({ onOpenChart }) {
  const { t } = useTranslation('faithCheckin');
  const [latest, setLatest] = useState(null);
  const [loading, setLoading] = useState(true);
  const { openCheckin } = useFaithCheckin();

  useEffect(() => {
    api.get('/api/faith-checkins?days=1')
      .then(d => setLatest(d.todayCheckin || null))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return null;

  return (
    <div className="fc-profile-block card" onClick={latest ? onOpenChart : openCheckin}>
      <div className="fc-pb-left">
        <div className="fc-pb-icon">
          {latest ? EMOJIS[latest.score-1] : '🌱'}
        </div>
        <div>
          <div className="fc-pb-title">{t('profileTitle')}</div>
          {latest ? (
            <div className="fc-pb-val" style={{color: COLORS[latest.score-1]}}>
              {Array.isArray(t('labelsShort')) ? t('labelsShort')[latest.score-1] : LABELS_SHORT[latest.score-1]} · {new Date(latest.checkedAt).toLocaleDateString()}
            </div>
          ) : (
            <div className="fc-pb-val" style={{color:'var(--text-lt)'}}>
              {t('noCheckin')}
            </div>
          )}
        </div>
      </div>
      <div className="fc-pb-arrow">→</div>
    </div>
  );
}
